import React, { useState, useEffect } from 'react';
import { lockScroll, unlockScroll } from '../utils/scrollbarUtils';

const PhoneNumberInput = ({ isOpen, onClose, onConfirm, selectedLanguage = 'ko' }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animatingIndex, setAnimatingIndex] = useState(-1);
  const inputRef = React.useRef(null);
  const animationTimeoutRef = React.useRef(null);
  
  // Translations
  const translations = {
    ko: {
      mainTitle: '휴대폰 뒷 번호<br />4자리를 입력해주세요',
      subTitle: '주문 구분용으로 사용할게요!'
    },
    en: {
      mainTitle: 'Enter last<br />4 digits of your phone',
      subTitle: 'For order identification!'
    },
    ja: {
      mainTitle: '携帯番号下<br />4桁を入力してください',
      subTitle: '注文識別用です！'
    },
    zh: {
      mainTitle: '请输入<br />手机号后4位',
      subTitle: '用于订单识别！'
    },
  };
  
  const t = translations[selectedLanguage] || translations.ko;

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(false);
      setIsVisible(true);
      
      // body 스크롤 방지
      lockScroll();
      
      // 히스토리 상태 추가
      window.history.pushState({ phoneInput: true }, '');
      
      // 다음 프레임에서 애니메이션 시작
      setTimeout(() => {
        setIsAnimating(true);
        // input에 포커스
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    } else {
      setIsAnimating(false);
      
      const timer = setTimeout(() => {
        setIsVisible(false);
        setPhoneNumber('');
        // 애니메이션이 완료된 후 body 스크롤 복원
        unlockScroll();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handlePopState = (e) => {
      if (isOpen) {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      // 애니메이션 타이머 정리
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const scaleUI = () => {
      const wrapper = document.querySelector('.phone-input-wrapper');
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };
    
    if (isVisible) {
      scaleUI();
      window.addEventListener('resize', scaleUI);
    }
    
    return () => {
      window.removeEventListener('resize', scaleUI);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, ''); // 숫자만 허용
    
    // 백스페이스로 인한 삭제 처리
    if (value.length < phoneNumber.length) {
      setPhoneNumber(value);
      // 애니메이션 상태 초기화
      setAnimatingIndex(-1);
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      return;
    }
    
    if (value.length <= 4) {
      // 새로 입력된 숫자의 인덱스에 애니메이션 트리거
      if (value.length > phoneNumber.length) {
        // 이전 애니메이션 타이머 취소
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
        }
        
        setAnimatingIndex(value.length - 1);
        animationTimeoutRef.current = setTimeout(() => {
          setAnimatingIndex(-1);
        }, 200);
      }
      
      setPhoneNumber(value);
      
      // 4자리 입력시 자동으로 확인
      if (value.length === 4) {
        setTimeout(() => {
          onConfirm(value);
        }, 100);
      }
    }
  };

  const getBoxStyle = (index) => {
    const currentLength = phoneNumber.length;
    
    if (index < currentLength) {
      // 입력 완료
      return 'completed';
    } else if (index === currentLength) {
      // 입력 대기
      return 'waiting';
    } else {
      // 입력 전
      return 'empty';
    }
  };

  return (
    <div className={`phone-input ${isAnimating ? 'open' : ''}`}>
      <div className="phone-input-wrapper">
        <div className="back-button-container">
          <button className="back-button" onClick={onClose}>
            <svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.625 13.5H21.375M5.625 13.5L12.375 20.25M5.625 13.5L12.375 6.75" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="phone-input-content">
          <div className="phone-input-header">
          <h2 className="main-title" dangerouslySetInnerHTML={{ __html: t.mainTitle }} />
            <p className="sub-title">{t.subTitle}</p>
          </div>

          <div className="phone-number-display" onClick={() => inputRef.current?.focus()}>
            {[0, 1, 2, 3].map((index) => (
              <div key={index} className={`number-box ${getBoxStyle(index)} ${animatingIndex === index ? 'animating' : ''}`}>
                <span className="number-text">
                  {phoneNumber[index] || ''}
                </span>
              </div>
            ))}
            <input
              ref={inputRef}
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              value={phoneNumber}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                // 백스페이스 키 처리
                if (e.key === 'Backspace' && phoneNumber.length > 0) {
                  e.preventDefault();
                  setPhoneNumber(phoneNumber.slice(0, -1));
                }
              }}
              maxLength="4"
              className="hidden-input"
              autoComplete="off"
            />
          </div>
        </div>
      </div>

      <style jsx="true">{`
        .phone-input {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: white;
          z-index: 1001;
          transform: translateX(100%);
          transition: transform 0.15s cubic-bezier(0.4, 0.0, 0.2, 1);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          height: 100vh;
          height: 100dvh;
          height: -webkit-fill-available;
        }

        .phone-input.open {
          transform: translateX(0);
        }

        .phone-input-wrapper {
          width: 360px;
          height: 100%;
          margin: 0 auto;
          position: relative;
          display: flex;
          flex-direction: column;
          transform-origin: top center;
        }
        
        @media screen and (min-width: 361px) {
          .phone-input-wrapper {
            zoom: calc(100vw / 360);
          }
        }
        
        @media screen and (min-width: 540px) {
          .phone-input-wrapper {
            zoom: 1.5;
          }
        }
        
        @media screen and (max-width: 360px) {
          .phone-input-wrapper {
            width: 100vw;
            zoom: calc(100vw / 360);
          }
        }

        .back-button-container {
          position: fixed;
          top: env(safe-area-inset-top, 0px);
          left: 16px;
          z-index: 10;
          padding-top: 14px;
        }
        
        .back-button {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          width: 27px;
          height: 27px;
          display: block;
        }
        
        .back-button svg {
          width: 100%;
          height: 100%;
          display: block;
        }

        .phone-input-content {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding-top: 130px;
        }

        .phone-input-header {
          margin-top: 90px;
          text-align: center;
        }

        .main-title {
          font-size: 24px;
          font-weight: 700;
          line-height: 29px;
          margin: 0 auto;
          display: block;
          text-align: center;
          padding: 0 20px;
        }

        .sub-title {
          margin-top: 13px;
          font-size: 14px;
          font-weight: 700;
          line-height: 17px;
          color: #FDCD27;
          height: 17px;
        }

        .phone-number-display {
          display: flex;
          gap: 6px;
          margin-top: 43px;
          cursor: pointer;
        }

        .number-box {
          width: 56px;
          height: 70px;
          border: 2px solid #F4F4F4;
          border-radius: 10px;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 42px;
          font-weight: 500;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .number-text {
          display: inline-block;
          transition: transform 0.3s ease;
        }

        /* 입력 완료 상태 */
        .number-box.completed {
          background: #F4F4F4;
        }

        /* 입력 애니메이션 상태 */
        .number-box.animating {
          animation: fillBackground 0.15s ease-out forwards;
        }

        .number-box.animating .number-text {
          animation: scaleNumber 0.2s ease-out;
        }

        /* 입력 대기 상태 */
        .number-box.waiting {
          animation: borderBlink 1.5s ease-in-out infinite;
        }
        
        @keyframes borderBlink {
          0%, 100% {
            border-color: #FDCD27;
          }
          50% {
            border-color: #F4F4F4;
          }
        }

        /* 입력 전 상태 (기본값) */
        .number-box.empty {
          border-color: #F4F4F4;
          background: white !important;
        }

        @keyframes fillBackground {
          0% {
            background: white;
          }
          100% {
            background: #F4F4F4;
          }
        }

        @keyframes scaleNumber {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .hidden-input {
          position: fixed;
          left: -9999px;
          top: 50%;
          width: 1px;
          height: 1px;
          opacity: 0;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

export default PhoneNumberInput;